import React, { useState } from 'react';
import { ChevronDown, HelpCircle } from 'lucide-react';
import { motion } from 'framer-motion';

const faqs = [
  {
    question: "What types of renovation projects do you handle?",
    answer: "We handle a wide range of renovation projects including kitchen remodeling, bathroom renovations, home additions, basement finishing, custom carpentry, deck and patio construction, interior renovations, and general home improvements. From small updates to complete home transformations.",
    category: "services"
  },
  {
    question: "How do I get started with my renovation project?",
    answer: "Getting started is easy! Contact us for a free consultation where we'll discuss your vision, assess your space, and provide recommendations. We'll walk you through the entire process from initial planning to final completion, ensuring your project runs smoothly.",
    category: "process"
  },
  {
    question: "How long does a typical renovation project take?",
    answer: "Project timelines vary depending on the scope and complexity of work. Small bathroom updates might take 1-2 weeks, while complete kitchen renovations typically take 4-8 weeks. Larger projects like home additions can take several months. We provide detailed timelines during planning.",
    category: "timeline"
  },
  {
    question: "What should I expect during the renovation process?",
    answer: "We maintain clear communication throughout your project with regular updates and progress reports. Our team works efficiently to minimize disruption to your daily routine. We protect your home with dust barriers and clean up daily to keep your space as comfortable as possible.",
    category: "process"
  },
  {
    question: "Do you help with design and material selection?",
    answer: "Absolutely! We work with you to select materials, finishes, and fixtures that match your style and budget. Our team can provide design guidance and recommendations based on current trends and what works best for your space and lifestyle.",
    category: "design"
  },
  {
    question: "Can you work around my schedule and lifestyle?",
    answer: "Yes, we understand that renovations can be disruptive. We work with you to minimize inconvenience by scheduling work during convenient hours, maintaining access to essential areas when possible, and coordinating the project timeline around your needs.",
    category: "process"
  },
  {
    question: "What makes your renovation approach different?",
    answer: "We focus on quality craftsmanship, clear communication, and attention to detail. Every project receives personalized attention from planning through completion. We use premium materials, skilled craftsmen, and maintain the highest standards throughout the renovation process.",
    category: "quality"
  },
  {
    question: "How do you ensure quality throughout the project?",
    answer: "Quality control is built into every step of our process. We conduct regular inspections, use skilled craftsmen, source premium materials, and maintain strict standards. Our project managers oversee every detail to ensure your renovation meets our high-quality standards.",
    category: "quality"
  },
  {
    question: "What happens if I want to make changes during construction?",
    answer: "We understand that ideas can evolve during a project. We're flexible and can accommodate reasonable changes when possible. We'll discuss any implications for timeline and budget before proceeding, ensuring you're fully informed about any modifications.",
    category: "process"
  },
  {
    question: "Do you clean up after the work is completed?",
    answer: "Yes, we handle all cleanup and debris removal as part of our service. We maintain a clean work environment throughout the project and perform a thorough final cleanup when the work is complete, leaving your newly renovated space ready to enjoy.",
    category: "process"
  }
];

const categoryColors = {
  services: 'bg-blue-400',
  process: 'bg-green-400',
  timeline: 'bg-purple-400',
  design: 'bg-orange-400',
  quality: 'bg-yellow-400'
};

export function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="py-24 md:py-32 bg-zinc-800/30 relative">
      <div className="max-w-4xl mx-auto px-6">
        <motion.div 
          initial={{ y: 20, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="flex items-center justify-center gap-2 mb-4">
            <HelpCircle className="h-5 w-5 text-yellow-400" />
            <h2 className="section-subheading">FAQ</h2>
          </div>
          <h3 className="section-heading mb-6 relative">
            Frequently Asked Questions
            <motion.div
              initial={{ width: 0 }}
              whileInView={{ width: "200px" }}
              transition={{ duration: 1, delay: 0.5 }}
              viewport={{ once: true }}
              className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 h-1 bg-gradient-to-r from-transparent via-yellow-400 to-transparent"
            />
          </h3>
          <p className="text-zinc-400 max-w-2xl mx-auto">
            Common questions about our renovation services. Don't see your question? Contact us for more information.
          </p>
        </motion.div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="bg-zinc-800/50 rounded-xl border border-zinc-700/50 hover:border-yellow-400/30 transition-all duration-300"
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full px-6 py-6 text-left flex items-center justify-between hover:bg-zinc-700/30 transition-colors duration-200 rounded-xl"
              >
                <div className="flex items-start gap-3 flex-1">
                  <div className={`w-2 h-2 rounded-full ${categoryColors[faq.category as keyof typeof categoryColors]} mt-3 flex-shrink-0`} />
                  <h4 className="text-lg text-zinc-100 pr-4">{faq.question}</h4>
                </div>
                <ChevronDown 
                  className={`h-5 w-5 text-yellow-400 transition-transform duration-300 ${
                    openIndex === index ? 'rotate-180' : ''
                  }`} 
                />
              </button>
              
              {openIndex === index && (
                <div className="px-6 pb-6 border-t border-zinc-700/30">
                  <div className="pt-4">
                    <p className="text-zinc-400 leading-relaxed">{faq.answer}</p>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="text-center mt-12">
          <p className="text-zinc-400 mb-6">
            Have more questions about your renovation project?
          </p>
          <a 
            href="tel:9149606433"
            className="inline-flex items-center gap-2 px-8 py-4 bg-yellow-400 text-zinc-900 rounded-xl hover:bg-yellow-500 transition-all duration-300"
          >
            <span className="text-sm tracking-wider">Call (914) 960-6433</span>
          </a>
        </div>
      </div>
    </section>
  );
}